import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, Mail, Phone } from 'lucide-react';
import { CONTACT_INFO } from '../data/mock';

const Section = ({ number, title, children }) => (
  <section className="border-t border-black/10 pt-8 mt-8">
    <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-semibold mb-2">SECTION {number}</p>
    <h2
      className="font-serif text-2xl md:text-3xl text-[#1c1e20] mb-4 leading-tight"
      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
    >
      {title}
    </h2>
    <div className="text-[#3c3c3c] leading-[1.85] text-[15px] space-y-4 hipaa-prose">
      {children}
    </div>
  </section>
);

const Row = ({ title, children }) => (
  <div>
    <h3 className="text-[#1c1e20] font-semibold text-[15px] mb-1">{title}</h3>
    <div className="text-[#3c3c3c]">{children}</div>
  </div>
);

const HipaaNotice = () => (
  <div className="min-h-[calc(100vh-160px)] bg-[#faf6f0] py-14 md:py-20 px-6" data-testid="hipaa-notice-page">
    <style>{`
      .hipaa-prose ul { list-style: disc; padding-left: 22px; margin-top: 4px; }
      .hipaa-prose ul li { margin-bottom: 4px; }
    `}</style>
    <div className="max-w-[880px] mx-auto">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="w-14 h-14 rounded-full bg-white border border-[#c9a961] flex items-center justify-center mx-auto mb-5">
          <ShieldCheck size={22} className="text-[#c9a961]" strokeWidth={1.6} />
        </div>
        <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-3">HIPAA</p>
        <h1
          className="font-serif text-4xl md:text-5xl leading-[1.05] text-[#1c1e20]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
        >
          Notice of Privacy Practices
        </h1>
        <div className="w-16 h-[2px] bg-[#c9a961] mx-auto my-5" />
        <p className="text-[12px] text-[#6b6b6b] tracking-wide">
          EFFECTIVE DATE: SEPTEMBER 25, 2026
        </p>
      </div>

      {/* Body */}
      <article className="bg-white border border-black/5 p-8 md:p-12 shadow-sm">
        {/* Banner */}
        <div className="bg-[#141618] text-white p-6 md:p-7 mb-6">
          <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-semibold mb-2">
            YOUR INFORMATION. YOUR RIGHTS. OUR RESPONSIBILITIES.
          </p>
          <p className="text-[13px] tracking-[0.05em] text-white/90 leading-relaxed">
            THIS NOTICE DESCRIBES HOW MEDICAL INFORMATION ABOUT YOU MAY BE USED AND DISCLOSED AND HOW
            YOU CAN GET ACCESS TO THIS INFORMATION. <strong className="text-[#c9a961]">PLEASE REVIEW IT CAREFULLY.</strong>
          </p>
        </div>

        <div className="text-[#3c3c3c] leading-[1.85] text-[15px] space-y-4">
          <p>
            Aurelia (&ldquo;Aurelia,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects the
            privacy of your health information and is committed to protecting the information
            entrusted to us.
          </p>
          <p>
            This Notice of Privacy Practices (&ldquo;Notice&rdquo;) explains how Aurelia may use and
            disclose protected health information (&ldquo;PHI&rdquo;), when applicable, and describes
            your rights regarding your health information.
          </p>
          <p>
            Depending on the services provided and Aurelia&rsquo;s relationship with a healthcare
            provider, laboratory, or other covered entity, certain information may be subject to the
            federal Health Insurance Portability and Accountability Act (&ldquo;HIPAA&rdquo;) and other
            applicable privacy laws.
          </p>
        </div>

        <Section number="1" title="Your privacy rights">
          <p>You have certain rights regarding your health information. Depending on applicable law and Aurelia&rsquo;s role in providing services, you may have the right to:</p>
          <Row title="Request access to your health information">
            <p>You may request access to certain health information maintained by Aurelia.</p>
          </Row>
          <Row title="Request a correction">
            <p>You may ask Aurelia to correct health information that you believe is inaccurate or incomplete, subject to applicable legal requirements.</p>
          </Row>
          <Row title="Request confidential communications">
            <p>You may request that Aurelia communicate with you about your health information through a particular method or at a particular location when permitted by law.</p>
          </Row>
          <Row title="Request restrictions">
            <p>You may ask Aurelia to limit how your health information is used or disclosed.</p>
            <p>Aurelia is not required to agree to every requested restriction unless applicable law requires it.</p>
          </Row>
          <Row title="Request an accounting of certain disclosures">
            <p>You may request information about certain disclosures of your health information made by Aurelia, subject to applicable exceptions and limitations.</p>
          </Row>
          <Row title="Receive a copy of this Notice">
            <p>You may request a paper copy of this Notice at any time. You may also access the current version through our website.</p>
          </Row>
          <Row title="Choose someone to act on your behalf">
            <p>If you have legally authorized a personal representative, such as a healthcare power of attorney or legal guardian, that individual may exercise applicable privacy rights on your behalf.</p>
            <p>Aurelia may verify the person&rsquo;s authority before releasing information.</p>
          </Row>
          <Row title="File a privacy complaint">
            <p>You may file a complaint with Aurelia or with the U.S. Department of Health and Human Services Office for Civil Rights if you believe your privacy rights have been violated.</p>
            <p>Aurelia will not retaliate against you for filing a privacy complaint.</p>
          </Row>
        </Section>

        <Section number="2" title="How we may use and disclose your health information">
          <p>When applicable, Aurelia may use or disclose your health information without your written authorization as permitted or required by law.</p>
          <Row title="Treatment">
            <p>We may use or disclose information to assist with your healthcare or coordinate services. For example, information related to specimen collection may be provided to an ordering healthcare provider or laboratory when necessary to perform requested testing or coordinate your care.</p>
          </Row>
          <Row title="Payment">
            <p>We may use or disclose information when necessary to obtain payment for services. For example, information may be provided to an applicable healthcare organization, laboratory, insurer, or payment-related service provider when permitted by law.</p>
          </Row>
          <Row title="Healthcare operations">
            <p>We may use or disclose information for business and healthcare operations necessary to provide and maintain our services. Examples may include quality improvement, training, administrative activities, compliance, auditing, and coordinating services with applicable healthcare organizations or laboratories.</p>
          </Row>
          <Row title="Laboratory testing">
            <p>When you request laboratory services, specimens and information necessary to perform the requested testing may be provided to the appropriate laboratory or testing organization.</p>
            <p>Laboratories may have their own privacy practices and legal obligations.</p>
          </Row>
          <Row title="Individuals involved in your care">
            <p>When permitted by law, Aurelia may disclose limited health information to a family member, caregiver, or other person involved in your care or payment for your care when appropriate.</p>
          </Row>
          <Row title="Required by law">
            <p>Aurelia may use or disclose health information when required by federal, state, or local law.</p>
          </Row>
          <Row title="Public health activities">
            <p>We may disclose information for legally authorized public health activities, such as reporting certain diseases, injuries, or other conditions when required or permitted by law.</p>
          </Row>
          <Row title="Abuse, neglect, or domestic violence">
            <p>We may disclose information to appropriate authorities when legally permitted or required to report suspected abuse, neglect, or domestic violence.</p>
          </Row>
          <Row title="Health oversight">
            <p>We may disclose information to government agencies authorized to conduct audits, investigations, inspections, or other oversight activities.</p>
          </Row>
          <Row title="Legal proceedings">
            <p>We may disclose information in response to a court order, subpoena, warrant, or other lawful process when permitted or required by law.</p>
          </Row>
          <Row title="Law enforcement">
            <p>We may disclose information to law enforcement when permitted or required by applicable law.</p>
          </Row>
          <Row title="Serious threats to health or safety">
            <p>We may use or disclose information when necessary to prevent or lessen a serious and imminent threat to the health or safety of an individual or the public, when permitted by law.</p>
          </Row>
          <Row title="Workers' compensation">
            <p>We may disclose health information as authorized by and to the extent necessary to comply with workers&rsquo; compensation laws and similar programs.</p>
          </Row>
        </Section>

        <Section number="3" title="Uses requiring your authorization">
          <p>Certain uses and disclosures of health information require your written authorization when applicable. These may include certain:</p>
          <ul>
            <li>Marketing activities</li>
            <li>Disclosures involving the sale of protected health information</li>
            <li>Other uses or disclosures for which applicable law requires authorization</li>
          </ul>
          <p>If you provide written authorization, you may generally revoke that authorization in writing, except to the extent Aurelia or another authorized party has already relied upon it.</p>
        </Section>

        <Section number="4" title="We do not sell your personal information">
          <p><strong>Aurelia does not sell your personal information for commercial purposes.</strong></p>
          <p>We may use or disclose information as necessary to provide services, coordinate laboratory testing, operate our business, comply with legal obligations, or as otherwise permitted by applicable law.</p>
        </Section>

        <Section number="5" title="Your right to request restrictions">
          <p>You may request that Aurelia restrict certain uses or disclosures of your health information.</p>
          <p>If you pay for a service or healthcare item completely out of pocket and request that information not be disclosed to your health plan for payment or healthcare operations purposes, Aurelia will comply with the request when required by applicable law, unless another legal requirement requires disclosure.</p>
        </Section>

        <Section number="6" title="Our responsibilities">
          <p>Aurelia is committed to protecting the privacy and security of health information that we are legally required to protect.</p>
          <p>Where applicable, Aurelia is required to:</p>
          <ul>
            <li>Maintain the privacy of protected health information</li>
            <li>Provide individuals with this Notice describing our legal duties and privacy practices</li>
            <li>Follow the terms of the Notice currently in effect</li>
            <li>Notify affected individuals as required by law if a breach occurs involving unsecured protected health information</li>
          </ul>
          <p>Aurelia will not use or disclose your health information for purposes other than those described in this Notice or otherwise permitted by law without the appropriate authorization.</p>
        </Section>

        <Section number="7" title="Changes to this Notice">
          <p>Aurelia reserves the right to change this Notice and make the revised Notice applicable to health information we already maintain as well as information received in the future, when permitted by law.</p>
          <p>The current Notice will be available on our website and upon request.</p>
          <p>The effective date will appear at the beginning of the Notice.</p>
        </Section>

        <Section number="8" title="Questions or privacy concerns">
          <p>If you have questions about this Notice, want to exercise an applicable privacy right, or wish to file a complaint with Aurelia, please contact:</p>
          <div className="bg-[#faf6f0] border-l-2 border-[#c9a961] p-5 mt-3 space-y-1">
            <p className="text-[13px] tracking-[0.22em] text-[#8a7132] font-semibold">AURELIA</p>
            <p className="text-[#3c3c3c] text-[14px]">Atlanta, Georgia</p>
            <p className="text-[#3c3c3c] text-[14px]">Privacy Contact: Ryleigh Davidson, CPT &mdash; Privacy Officer</p>
            <p className="text-[#3c3c3c] text-[14px] flex items-center gap-2">
              <Mail size={13} className="text-[#c9a961]" />
              <a href={`mailto:${CONTACT_INFO.email}`} className="text-[#8a7132] underline hover:text-[#c9a961]">{CONTACT_INFO.email}</a>
            </p>
            <p className="text-[#3c3c3c] text-[14px] flex items-center gap-2">
              <Phone size={13} className="text-[#c9a961]" />
              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9+]/g, '')}`} className="text-[#8a7132] underline hover:text-[#c9a961]">{CONTACT_INFO.phone}</a>
            </p>
          </div>
          <p className="mt-4">You may also contact the U.S. Department of Health and Human Services Office for Civil Rights regarding a HIPAA privacy complaint.</p>
          <div className="bg-[#faf6f0] border-l-2 border-[#c9a961] p-5 mt-3">
            <p className="text-[13px] tracking-[0.22em] text-[#8a7132] font-semibold mb-2">U.S. HHS OFFICE FOR CIVIL RIGHTS</p>
            <p className="text-[#3c3c3c] text-[14px] leading-relaxed">
              200 Independence Avenue, S.W.<br/>
              Washington, D.C. 20201<br/>
              Phone: <a href="tel:18776966775" className="text-[#8a7132] underline hover:text-[#c9a961]">1-877-696-6775</a><br/>
              <a href="https://www.hhs.gov/ocr/privacy/hipaa/complaints/" target="_blank" rel="noopener noreferrer" className="text-[#8a7132] underline hover:text-[#c9a961]">
                hhs.gov/ocr/privacy/hipaa/complaints
              </a>
            </p>
          </div>
          <p className="mt-4">Complaints may also be submitted through the HHS Office for Civil Rights website.</p>
          <p><strong>Aurelia will not retaliate against you for filing a privacy complaint.</strong></p>
        </Section>

        <Section number="9" title="Acknowledgment of receipt">
          <p>I acknowledge that I have received or been offered a copy of Aurelia&rsquo;s Notice of Privacy Practices.</p>
          <p>I understand that this acknowledgment confirms receipt of the Notice and does not constitute authorization for any use or disclosure of my health information beyond what is permitted or required by law.</p>
          <div className="bg-[#faf6f0] border border-black/5 p-6 mt-5 space-y-4 text-[14px] text-[#3c3c3c]">
            <div>
              <p className="text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-1">PATIENT / CUSTOMER NAME</p>
              <div className="border-b border-black/25 h-6" />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-1">SIGNATURE</p>
              <div className="border-b border-black/25 h-6" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-1">DATE</p>
                <div className="border-b border-black/25 h-6" />
              </div>
            </div>

            <p className="text-[12px] tracking-[0.18em] text-[#8a7132] font-semibold pt-3 border-t border-black/10">IF SIGNED BY PERSONAL REPRESENTATIVE</p>
            <div>
              <p className="text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-1">NAME</p>
              <div className="border-b border-black/25 h-6" />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-1">RELATIONSHIP / AUTHORITY</p>
              <div className="border-b border-black/25 h-6" />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-1">SIGNATURE</p>
              <div className="border-b border-black/25 h-6" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] tracking-[0.28em] text-[#6b6b6b] font-semibold mb-1">DATE</p>
                <div className="border-b border-black/25 h-6" />
              </div>
            </div>
          </div>
          <p className="text-[12px] text-[#6b6b6b] mt-3">Prefer to acknowledge digitally? Complete our <Link to="/patient-forms" className="text-[#8a7132] underline hover:text-[#c9a961]">Client Consent Form</Link> — HIPAA acknowledgment is included.</p>
        </Section>

        <p className="text-[13px] text-[#6b6b6b] leading-relaxed mt-10 pt-6 border-t border-black/10 italic">
          <strong>Important Notice:</strong> This document is intended as a business template and
          should be reviewed by a qualified healthcare/privacy attorney before Aurelia publishes or
          distributes it. Whether Aurelia is legally required to maintain its own HIPAA Notice of
          Privacy Practices depends on its specific role and relationships with covered entities. A
          business associate, by itself, generally is not required by HIPAA to create its own NPP.
        </p>
      </article>

      {/* Back to consent */}
      <div className="mt-10 text-center">
        <Link
          to="/patient-forms"
          data-testid="back-to-consent"
          className="inline-flex items-center gap-2 border border-[#1c1e20] hover:bg-[#1c1e20] hover:text-white transition-colors text-[#1c1e20] tracking-[0.22em] text-[11px] font-semibold px-7 py-3"
        >
          <ArrowLeft size={14} /> RETURN TO CONSENT FORM
        </Link>
      </div>
    </div>
  </div>
);

export default HipaaNotice;
